<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { loadEventTranslations, createEventTranslation, updateEventTranslation } from '@views/Event/Event.access';
    import { type Event, type EventTranslation } from '@views/Event/EventTypes';
    import { trackSaveKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        event: Event;
        updateEnabled: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'textChanged', data: { key: string; value: string }): void;
    }>();

    const trackSave = inject(trackSaveKey)!;

    type LandingPageTranslation = {
        key: string;
        label: string;
        defaultValue: string;
        currentValue: string;
        savedValue: string;
        emptyAllowed: boolean;
        translation: EventTranslation | null;
    };

    const state = reactive<{ translations: LandingPageTranslation[] }>({ translations: [] });

    async function loadEventLanguageResources(languageId: string): Promise<Record<string, string>> {
        const response = await fetch('/Js/ResourcesJson?languageId=' + languageId);
        if (!response.ok) throw new Error(`Failed to load resources for ${languageId}`);
        return response.json();
    }

    onMounted(async () => {
        // Defaults come from the event's own language, not the admin UI language (same as the old KO editor).
        const eventCulture = props.event.Culture || props.event.LanguageId;
        let eventLang: Record<string, string>;
        let saved: EventTranslation[];
        try {
            [eventLang, saved] = await Promise.all([loadEventLanguageResources(eventCulture), loadEventTranslations(props.event.EventId)]);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
            return;
        }

        const create = (key: string, label: string, defaultValue: string, emptyAllowed: boolean): LandingPageTranslation => {
            const translation = saved.find((t) => t.Label === key) ?? null;
            const value = translation?.Value ?? defaultValue;
            return { key, label, defaultValue, currentValue: value, savedValue: value, emptyAllowed, translation };
        };

        state.translations = [
            create('eventInvitationText', $Resources.EventInvitationTextLabel, eventLang.eventLandingTemplateHeadingInvite, true),
            create('eventSignUpText', $Resources.EventSignUpButtonTextLabel, eventLang.signUpHere, false),
            create('eventIsFullButtonText', $Resources.EventIsFullButtonTextLabel, eventLang.eventIsFullButtonText, false),
            create('eventWaitListIsFullInfoText', $Resources.EventWaitListIsFullInfoTextLabel, eventLang.eventWaitListIsFullInfoText, true),
        ];

        state.translations.forEach((t) => emit('textChanged', { key: t.key, value: t.currentValue }));
    });

    async function saveTranslation(data: LandingPageTranslation) {
        try {
            await trackSave(async () => {
                if (data.translation) {
                    data.translation.ObjectId = props.event.EventId;
                    data.translation.Label = data.key;
                    data.translation.Value = data.currentValue;
                    await updateEventTranslation(data.translation);
                } else {
                    data.translation = await createEventTranslation({ ObjectId: props.event.EventId, Label: data.key, Value: data.currentValue });
                }
            });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorTechnicalIssuesBody });
        }
    }

    const saveChains = new Map<LandingPageTranslation, Promise<void>>();

    function commit(data: LandingPageTranslation) {
        if (data.currentValue === data.savedValue) return;
        if (!data.emptyAllowed && data.currentValue.length === 0) return;
        data.savedValue = data.currentValue;
        emit('textChanged', { key: data.key, value: data.currentValue });
        // Saves of a text run one at a time, so an edit made while the first one creates the translation updates it instead of creating a duplicate.
        const previous = saveChains.get(data) ?? Promise.resolve();
        saveChains.set(
            data,
            previous.then(() => saveTranslation(data))
        );
    }

    function reset(data: LandingPageTranslation) {
        data.currentValue = data.defaultValue;
        commit(data);
    }
</script>

<template>
    <div class="event-card">
        <h3 class="event-card-heading">{{ $Resources.EventLandingPageTranslationsHeading }}</h3>

        <div v-for="data in state.translations" :key="data.key" class="form-group">
            <FormInput
                :id="data.key"
                :label="data.label"
                :required="!data.emptyAllowed"
                v-model="data.currentValue"
                :disabled="!updateEnabled"
                @onBlur="commit(data)">
                <template #button>
                    <button
                        type="button"
                        class="btn btn-default btn-flex text-sm"
                        :disabled="!updateEnabled || data.defaultValue === data.currentValue"
                        @click="reset(data)">
                        {{ $Resources.ResetBtn }}
                    </button>
                </template>
            </FormInput>

            <BsAlert
                v-if="!data.currentValue && !data.emptyAllowed"
                class="m-xs-t"
                :type="AlertTypes.Danger"
                :dismissible="false"
                :title="$Resources.EventLandingPageTranslationsEmptyNotAllowedHeading"
                :content="$Resources.EventLandingPageTranslationsEmptyNotAllowedBody" />
        </div>
    </div>
</template>
